import type { ProvisionRunStatus, ProvisionStartResponse } from '@frontend/types/provision';
import { clearEcosystemsCache } from '@frontend/api-clients/ecosystemsClient';

const KNOWN_SERVICES = ['gitea', 'nextcloud', 'openproject', 'woodpecker', 'collabra'] as const;
export type EcosystemService = (typeof KNOWN_SERVICES)[number];

export interface EcosystemProvisionPayload {
  id: string;
  displayName: string;
  memberGroup?: string;
  giteaOrg?: string;
  services?: EcosystemService[];
  /** Forward-compatible; not collected in v1 UI. */
  presentation?: Record<string, unknown>;
  flags?: { dryRun?: boolean; sync?: boolean };
}

async function adminOrchFetch<T>(
  method: string,
  path: string,
  token: string,
  body?: unknown
): Promise<{ ok: boolean; status: number; data: T }> {
  const res = await fetch(path, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const data = (await res.json().catch(() => ({}))) as T & { message?: string; code?: string };
  if (!res.ok) {
    const err = new Error(data.message || `Request failed: ${res.status}`) as Error & {
      code?: string;
      status?: number;
      correlationId?: string;
    };
    err.code = (data as { code?: string }).code;
    err.status = res.status;
    const cid = (data as { correlationId?: string }).correlationId;
    if (cid) err.correlationId = cid;
    throw err;
  }
  return { ok: true, status: res.status, data };
}

function normalizePayload(payload: EcosystemProvisionPayload): EcosystemProvisionPayload {
  const id = payload.id.trim();
  return {
    ...payload,
    id,
    memberGroup: (payload.memberGroup || `${id}-users`).trim(),
    giteaOrg: (payload.giteaOrg || id).trim(),
    services: payload.services?.length
      ? payload.services
      : (['gitea', 'nextcloud', 'woodpecker'] as EcosystemService[]),
  };
}

export async function previewEcosystemProvision(
  token: string,
  payload: EcosystemProvisionPayload
): Promise<{ plannedSteps: string[]; ecosystemId: string; correlationId?: string }> {
  const body = normalizePayload({ ...payload, flags: { ...payload.flags, dryRun: true } });
  const { data } = await adminOrchFetch<{
    plannedSteps?: string[];
    ecosystemId?: string;
    correlationId?: string;
    dryRun?: boolean;
  }>('POST', '/api/admin/ecosystems/provision', token, body);
  return {
    plannedSteps: data.plannedSteps || [],
    ecosystemId: data.ecosystemId || body.id,
    correlationId: data.correlationId,
  };
}

export async function startEcosystemProvision(
  token: string,
  payload: EcosystemProvisionPayload
): Promise<ProvisionStartResponse> {
  const body = normalizePayload(payload);
  const { data } = await adminOrchFetch<ProvisionStartResponse>(
    'POST',
    '/api/admin/ecosystems/provision',
    token,
    body
  );
  return data;
}

export async function getEcosystemProvisionStatus(
  token: string,
  correlationId: string
): Promise<ProvisionRunStatus> {
  const { data } = await adminOrchFetch<ProvisionRunStatus>(
    'GET',
    `/api/admin/ecosystems/provision/status/${encodeURIComponent(correlationId)}`,
    token
  );
  return data;
}

export function isProvisionTerminal(runState: string): boolean {
  return runState === 'completed' || runState === 'partial' || runState === 'failed';
}

export function onEcosystemProvisionTerminal(runState: string): void {
  if (runState === 'completed') {
    clearEcosystemsCache();
  }
}

export { KNOWN_SERVICES };
